/**
 * CampaignFormModal — edit a campaign the current user created.
 * Creator-only (backend also enforces ownership). Text/URL only for cover image
 * (no file upload — avoids /media/upload dependency).
 * Mirrors the field set of CreateCampaignPage.
 */
import React, { useState, useEffect } from 'react'
import { X, Loader2 } from 'lucide-react'
import { GovButton } from '@/components/layout/GovLayout'
import campaignService, { Campaign, CampaignCreateRequest } from '@/services/campaignService'

const CAMPAIGN_TYPES = [
  { value: 'AWARENESS', label: 'Awareness Campaign' },
  { value: 'SCREENING', label: 'Health Screening' },
  { value: 'HEALTH_CAMP', label: 'Health Camp' },
  { value: 'SUPPORT', label: 'Patient Support' },
  { value: 'VACCINATION', label: 'Vaccination Drive' },
  { value: 'COMMUNITY_OUTREACH', label: 'Community Outreach' },
  { value: 'EDUCATION', label: 'Education Program' }
]

interface CampaignFormModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  campaign: Campaign | null
}

// ISO "2026-01-01T00:00:00" -> datetime-local "2026-01-01T00:00"
const toLocalInput = (iso?: string): string => {
  if (!iso) return ''
  return iso.slice(0, 16)
}
// datetime-local value -> ISO string (or undefined if empty)
const toIso = (val: string): string | undefined => {
  if (!val) return undefined
  return new Date(val).toISOString()
}

const CampaignFormModal: React.FC<CampaignFormModalProps> = ({ open, onClose, onSuccess, campaign }) => {
  const [formData, setFormData] = useState<CampaignCreateRequest>({
    title: '', description: '', type: 'AWARENESS',
    startDate: undefined, endDate: undefined,
    targetAmount: undefined, targetParticipants: undefined,
    location: '', coverImage: '', isFeatured: false
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !campaign) return
    setError(null)
    setFormData({
      title: campaign.title || '',
      description: campaign.description || '',
      type: (campaign.type as CampaignCreateRequest['type']) || 'AWARENESS',
      startDate: undefined,
      endDate: undefined,
      targetAmount: campaign.targetAmount,
      targetParticipants: campaign.targetParticipants,
      location: campaign.location || '',
      coverImage: campaign.coverImage || '',
      isFeatured: campaign.isFeatured ?? false
    })
  }, [open, campaign])

  // datetime-local state (kept as plain strings for the inputs) — declared BEFORE the
  // early-return guard so hook count is stable across renders (React hooks rule).
  const [startLocal, setStartLocal] = useState('')
  const [endLocal, setEndLocal] = useState('')
  useEffect(() => {
    if (!open || !campaign) return
    setStartLocal(campaign.startDate ? toLocalInput(campaign.startDate) : '')
    setEndLocal(campaign.endDate ? toLocalInput(campaign.endDate) : '')
  }, [open, campaign])

  if (!open || !campaign) return null

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'targetAmount' || name === 'targetParticipants'
        ? value ? Number(value) : undefined
        : value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!formData.title.trim()) { setError('Title is required'); return }
    setLoading(true)
    try {
      await campaignService.updateCampaign(campaign.id, {
        ...formData,
        startDate: toIso(typeof formData.startDate === 'string' ? formData.startDate as string : '') || (campaign.startDate ? new Date(campaign.startDate).toISOString() : undefined),
        endDate: toIso(typeof formData.endDate === 'string' ? formData.endDate as string : '') || (campaign.endDate ? new Date(campaign.endDate).toISOString() : undefined)
      })
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update campaign')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-gray-900">Edit Campaign</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input name="title" value={formData.title} onChange={handleChange} required maxLength={200}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
            <select name="type" value={formData.type} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
              {CAMPAIGN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input type="datetime-local" value={startLocal} onChange={e => { setStartLocal(e.target.value); setFormData(prev => ({ ...prev, startDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })) }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input type="datetime-local" value={endLocal} onChange={e => { setEndLocal(e.target.value); setFormData(prev => ({ ...prev, endDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })) }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Amount (₹)</label>
              <input type="number" name="targetAmount" value={formData.targetAmount ?? ''} onChange={handleChange} min={0}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Participants</label>
              <input type="number" name="targetParticipants" value={formData.targetParticipants ?? ''} onChange={handleChange} min={0}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input name="location" value={formData.location} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image URL</label>
            <input name="coverImage" value={formData.coverImage ?? ''} onChange={handleChange} placeholder="https://… (optional)"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" name="isFeatured" checked={!!formData.isFeatured} onChange={e => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))} className="rounded border-gray-300" />
            Featured campaign
          </label>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <GovButton variant="ghost" onClick={onClose} type="button">Cancel</GovButton>
            <GovButton disabled={loading} type="submit">
              {loading ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Saving…</span> : 'Save Changes'}
            </GovButton>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CampaignFormModal